/**
 * PURE. The creatures §B7.1 primitive library: `loft` (rings along a spine, ring stagger, zipper triangulation between
 * rings of any side count, fan/point caps, ring creases, ≤ 3 % mirrored hash jitter), `extrudeProfile`, `disc`, `strip`
 * (double-sided), `box`. Every primitive writes one `Surface` (shared vertices) and is deterministic.
 *
 * Winding: counter-clockwise seen from outside (outward cross(b − a, c − a)); verified by tests/creature/mesh.test.ts.
 */
import { Surface, type TriOpts, type V3, type Vert } from './builder';

export type { V3 };
export const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scl = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
export const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const len = (a: V3): number => Math.hypot(a[0], a[1], a[2]);
export const nrm = (a: V3): V3 => { const l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
export const lerp3 = (a: V3, b: V3, t: number): V3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
export const mirrorX = (a: V3): V3 => [-a[0], a[1], a[2]];

/** Deterministic hash of integers → [−1, 1). */
export function hashSigned(...xs: number[]): number {
  let h = 0x811c9dc5;
  for (const x of xs) {
    h ^= x | 0; h = Math.imul(h, 0x01000193);
    h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  }
  return ((h >>> 0) / 4294967296) * 2 - 1;
}
/** Mirror-symmetric jitter of a point (keyed on |x|, so the left and right halves move identically). */
export const symJitter = (seed: number, p: V3): number =>
  hashSigned(seed, Math.round(Math.abs(p[0]) * 2000), Math.round(p[1] * 2000), Math.round(p[2] * 2000));

export interface Skin { bA: number; bB: number; wA: number }
export const rigid = (b: number): Skin => ({ bA: b, bB: b, wA: 1 });
/** Two-bone blend: `t` = weight of bone B (0 → all A). */
export const blend = (a: number, b: number, t: number): Skin => {
  const w = Math.max(0, Math.min(1, 1 - t));
  if (w >= 0.999) return rigid(a);
  if (w <= 0.001) return rigid(b);
  return { bA: a, bB: b, wA: w };
};

export interface Ring {
  c: V3;
  /** Radius along the side axis and along the front axis. */
  rs: number; rf: number;
  skin: Skin;
  lw?: number;
  /** Override side count for this ring. */
  sides?: number;
  /** Extra angular offset (fraction of one side). */
  off?: number;
  /** Per-angle radius multiplier / point deformer (θ in turns, 0 = side axis, 0.25 = front). */
  shape?: (theta: number) => number;
  deform?: (p: V3, theta: number, ringIndex: number) => V3;
  /** Per-vertex skin override. */
  skinAt?: (theta: number, p: V3) => Skin;
}

export interface LoftOpts {
  sides: number;
  /** Reference front direction (projected off the tangent): partV 0.25. */
  front: V3;
  stagger?: boolean;
  mat: number | ((seg: number, theta: number) => number);
  region: number | ((seg: number, theta: number) => number);
  capStart?: 'flat' | 'point' | 'none';
  capEnd?: 'flat' | 'point' | 'none';
  /** Distance of a 'point' cap tip beyond the end ring (along the tangent). */
  capStartLen?: number; capEndLen?: number;
  hull?: boolean;
  /** Ring index → crease the ring's edge loop (optionally only inside a θ sector [from, to] in turns). */
  creases?: Array<{ ring: number; from?: number; to?: number }>;
  /** Jitter amplitude as a fraction of the local radius (≤ 0.03) and its seed. */
  jitter?: number; seed?: number;
  /** Starting partU. */
  u0?: number;
  /** Explicit tangent override (e.g. a flat head axis). */
  tangent?: (i: number) => V3;
  capSkin?: Skin;
}

interface BuiltRing { idx: number[]; theta: number[] }

/** Lofts `rings` into `s` and returns the vertex indices per ring. */
export function loft(s: Surface, rings: Ring[], o: LoftOpts): BuiltRing[] {
  const n = rings.length;
  const built: BuiltRing[] = [];
  let u = o.u0 ?? 0;
  const tangents: V3[] = [];
  for (let i = 0; i < n; i++) {
    if (o.tangent) { tangents.push(nrm(o.tangent(i))); continue; }
    const a = rings[Math.max(0, i - 1)].c, b = rings[Math.min(n - 1, i + 1)].c;
    let t = sub(b, a);
    if (len(t) < 1e-9) t = [0, 1, 0];
    tangents.push(nrm(t));
  }
  for (let i = 0; i < n; i++) {
    const r = rings[i];
    if (i > 0) u += len(sub(r.c, rings[i - 1].c));
    const T = tangents[i];
    let F = sub(o.front, scl(T, dot(o.front, T)));
    if (len(F) < 1e-6) F = Math.abs(T[1]) < 0.9 ? sub([0, 1, 0], scl(T, T[1])) : sub([0, 0, 1], scl(T, T[2]));
    F = nrm(F);
    const Up = nrm(cross(F, T));
    const sides = r.sides ?? o.sides;
    const off = (r.off ?? 0) + (o.stagger && i % 2 === 1 ? 0.5 : 0);
    const idx: number[] = []; const theta: number[] = [];
    for (let j = 0; j < sides; j++) {
      const th = (j + off) / sides;
      const a = th * Math.PI * 2;
      const k = r.shape ? r.shape(((th % 1) + 1) % 1) : 1;
      let p = add(r.c, add(scl(Up, Math.cos(a) * r.rs * k), scl(F, Math.sin(a) * r.rf * k)));
      if (r.deform) p = r.deform(p, ((th % 1) + 1) % 1, i);
      if (o.jitter) {
        const jit = symJitter(o.seed ?? 1, p) * o.jitter * Math.min(r.rs, r.rf);
        const dir = sub(p, r.c);
        const dl = len(dir);
        if (dl > 1e-9) p = add(p, scl(dir, jit / dl));
      }
      const sk = r.skinAt ? r.skinAt(((th % 1) + 1) % 1, p) : r.skin;
      idx.push(s.vert({ p, u, v: ((th % 1) + 1) % 1, ...sk, lw: r.lw ?? 1 }));
      theta.push(th);
    }
    built.push({ idx, theta });
  }
  const matOf = (seg: number, th: number): number => (typeof o.mat === 'number' ? o.mat : o.mat(seg, ((th % 1) + 1) % 1));
  const regOf = (seg: number, th: number): number => (typeof o.region === 'number' ? o.region : o.region(seg, ((th % 1) + 1) % 1));
  const inSector = (cr: { from?: number; to?: number }, th: number): boolean => {
    if (cr.from === undefined || cr.to === undefined) return true;
    const t = ((th % 1) + 1) % 1;
    return cr.from <= cr.to ? t >= cr.from && t <= cr.to : t >= cr.from || t <= cr.to;
  };
  // Strips between consecutive rings (zipper).
  for (let i = 0; i + 1 < n; i++) {
    const A = built[i], B = built[i + 1];
    const creaseA = o.creases?.filter((c) => c.ring === i) ?? [];
    const creaseB = o.creases?.filter((c) => c.ring === i + 1) ?? [];
    zipper(A, B, (a0, a1, b0, kind, thMid) => {
      const info: TriOpts = { mat: matOf(i, thMid), region: regOf(i, thMid), hull: o.hull };
      if (kind === 'A') {
        // tri (a0, a1, b0): edge a0–a1 lies on ring A, opposite corner 2.
        if (creaseA.some((c) => inSector(c, thMid))) info.edge = 4;
        s.tri(a0, a1, b0, info);
      } else {
        // tri (a0, b1, b0) with b0 = previous B, b1 = next B: edge b1–b0 on ring B, opposite corner 0.
        if (creaseB.some((c) => inSector(c, thMid))) info.edge = 1;
        s.tri(a0, a1, b0, info);
      }
    });
  }
  // Caps.
  const cap = (ri: number, kind: 'flat' | 'point' | 'none' | undefined, capLen: number | undefined, start: boolean): void => {
    if (!kind || kind === 'none') return;
    const r = rings[ri];
    const T = tangents[ri];
    const ring = built[ri];
    const c = kind === 'point' ? add(r.c, scl(T, (start ? -1 : 1) * (capLen ?? Math.min(r.rs, r.rf)))) : r.c;
    const sk = o.capSkin ?? r.skin;
    const ci = s.vert({ p: c, u: u + (start ? 0 : capLen ?? 0), v: 0, ...sk, lw: r.lw ?? 1 });
    const m = ring.idx.length;
    for (let j = 0; j < m; j++) {
      const a = ring.idx[j], b = ring.idx[(j + 1) % m];
      const info: TriOpts = { mat: matOf(start ? 0 : n - 2, ring.theta[j]), region: regOf(start ? 0 : n - 2, ring.theta[j]), hull: o.hull };
      if (start) s.tri(ci, b, a, info); else s.tri(ci, a, b, info);
    }
  };
  cap(0, o.capStart, o.capStartLen, true);
  cap(n - 1, o.capEnd, o.capEndLen, false);
  return built;
}

/** Zipper triangulation between two rings of any side counts (angles in turns, increasing CCW around the tangent). */
function zipper(A: BuiltRing, B: BuiltRing, emit: (x: number, y: number, z: number, kind: 'A' | 'B', thMid: number) => void): void {
  const na = A.idx.length, nb = B.idx.length;
  const angA = (i: number): number => A.theta[0] + (i / na);
  // find the B vertex closest to A[0]
  let j0 = 0; let best = Infinity;
  for (let j = 0; j < nb; j++) {
    let d = B.theta[j] - A.theta[0];
    d -= Math.round(d);
    if (Math.abs(d) < best) { best = Math.abs(d); j0 = j; }
  }
  let base = B.theta[j0] - A.theta[0]; base -= Math.round(base);
  const angB = (j: number): number => A.theta[0] + base + (j / nb);
  let i = 0, j = 0;
  while (i < na || j < nb) {
    const nextA = i < na ? angA(i + 1) : Infinity;
    const nextB = j < nb ? angB(j + 1) : Infinity;
    const a0 = A.idx[i % na], b0 = B.idx[(j0 + j) % nb];
    if (nextA <= nextB) {
      const a1 = A.idx[(i + 1) % na];
      emit(a0, a1, b0, 'A', (angA(i) + nextA) / 2);
      i++;
    } else {
      const b1 = B.idx[(j0 + j + 1) % nb];
      emit(a0, b1, b0, 'B', (angB(j) + nextB) / 2);
      j++;
    }
  }
}

/** A flat (optionally domed) polygon fan: `outline` points around `center`, facing `normal` (CCW seen from the normal side). */
export function fan(s: Surface, center: V3, outline: V3[], skin: Skin | ((p: V3) => Skin), o: TriOpts & { lw?: number; u?: number }, centerLift: V3 = [0, 0, 0]): void {
  const sk = (p: V3): Skin => (typeof skin === 'function' ? skin(p) : skin);
  const c = add(center, centerLift);
  const ci = s.vert({ p: c, u: o.u ?? 0, v: 0, ...sk(c), lw: o.lw ?? 1 });
  const idx = outline.map((p, k) => s.vert({ p, u: o.u ?? 0, v: k / outline.length, ...sk(p), lw: o.lw ?? 1 }));
  for (let k = 0; k < idx.length; k++) s.tri(ci, idx[k], idx[(k + 1) % idx.length], o);
}

/** Polygon outline in a plane: `n` points of an ellipse (rx along `ax`, ry along `ay`) with optional per-angle shaping. */
export function ellipse(center: V3, ax: V3, ay: V3, rx: number, ry: number, n: number, shape?: (t: number) => [number, number], phase = 0): V3[] {
  const out: V3[] = [];
  for (let k = 0; k < n; k++) {
    const t = (k + phase) / n;
    const a = t * Math.PI * 2;
    let x = Math.cos(a) * rx, y = Math.sin(a) * ry;
    if (shape) { const [mx, my] = shape(t); x *= mx; y *= my; }
    out.push(add(center, add(scl(ax, x), scl(ay, y))));
  }
  return out;
}

/** Extrudes a closed 2D profile (in the plane of `ax`/`ay`, star-shaped around its centroid) by `depth` along `az`.
 *  Front cap faces +az. `skinAt` receives the 2D coordinates. */
export function extrudeProfile(s: Surface, origin: V3, ax: V3, ay: V3, az: V3, profile: Array<[number, number]>, depth: number,
  skinAt: (x: number, y: number) => Skin, o: { mat: number; region: number; lw?: number; hull?: boolean; edge?: boolean; backMat?: number; sideMat?: number }): { front: number[]; back: number[] } {
  const P = (x: number, y: number, z: number): V3 => add(origin, add(add(scl(ax, x), scl(ay, y)), scl(az, z)));
  // Orientation: make the profile CCW around +az (seen from the front).
  let area = 0;
  for (let k = 0; k < profile.length; k++) { const [x0, y0] = profile[k]; const [x1, y1] = profile[(k + 1) % profile.length]; area += x0 * y1 - x1 * y0; }
  const prof = area < 0 ? [...profile].reverse() : profile;
  const handed = dot(cross(ax, ay), az) >= 0;
  const cx = prof.reduce((a, p) => a + p[0], 0) / prof.length, cy = prof.reduce((a, p) => a + p[1], 0) / prof.length;
  const vtx = (x: number, y: number, z: number, u: number): number => s.vert({ p: P(x, y, z), u, v: 0, ...skinAt(x, y), lw: o.lw ?? 1 });
  const front = prof.map(([x, y]) => vtx(x, y, depth / 2, y));
  const back = prof.map(([x, y]) => vtx(x, y, -depth / 2, y));
  const fc = vtx(cx, cy, depth / 2, cy), bc = vtx(cx, cy, -depth / 2, cy);
  const m = prof.length;
  const tri = (a: number, b: number, c: number, info: TriOpts): void => (handed ? s.tri(a, b, c, info) : s.tri(a, c, b, info));
  for (let k = 0; k < m; k++) {
    const k1 = (k + 1) % m;
    tri(fc, front[k], front[k1], { mat: o.mat, region: o.region, hull: o.hull });
    tri(bc, back[k1], back[k], { mat: o.backMat ?? o.mat, region: o.region, hull: o.hull });
    if (depth > 0) {
      tri(front[k], back[k], back[k1], { mat: o.sideMat ?? o.mat, region: o.region, hull: o.hull, edge: o.edge ? 4 : 0 });
      tri(front[k], back[k1], front[k1], { mat: o.sideMat ?? o.mat, region: o.region, hull: o.hull });
    }
  }
  return { front, back };
}

/** A double-sided ribbon along `path` with per-point half-widths, widened along `side` (whiskers, feathers, cape edges). */
export function strip(s: Surface, path: V3[], widths: number[], side: V3 | ((i: number) => V3), skinAt: (i: number) => Skin, o: TriOpts & { lw?: number; doubleSided?: boolean }): void {
  const L: number[] = [], R: number[] = [];
  let u = 0;
  for (let i = 0; i < path.length; i++) {
    if (i > 0) u += len(sub(path[i], path[i - 1]));
    const sd = typeof side === 'function' ? side(i) : side;
    const w = widths[i];
    L.push(s.vert({ p: add(path[i], scl(sd, w)), u, v: 0, ...skinAt(i), lw: o.lw ?? 1 }));
    R.push(s.vert({ p: add(path[i], scl(sd, -w)), u, v: 1, ...skinAt(i), lw: o.lw ?? 1 }));
  }
  for (let i = 0; i + 1 < path.length; i++) {
    s.tri(L[i], R[i], R[i + 1], o); s.tri(L[i], R[i + 1], L[i + 1], o);
    if (o.doubleSided !== false) { s.tri(L[i], R[i + 1], R[i], { ...o, hull: false }); s.tri(L[i], L[i + 1], R[i + 1], { ...o, hull: false }); }
  }
}

/** An oriented box (bevel-free) from a centre and three half-axes. */
export function box(s: Surface, c: V3, ax: V3, ay: V3, az: V3, skin: Skin, o: TriOpts & { lw?: number }): void {
  const corners: V3[] = [];
  for (const z of [-1, 1]) for (const y of [-1, 1]) for (const x of [-1, 1]) corners.push(add(c, add(add(scl(ax, x), scl(ay, y)), scl(az, z))));
  const idx = corners.map((p) => s.vert({ p, u: 0, v: 0, ...skin, lw: o.lw ?? 1 }));
  const handed = dot(cross(ax, ay), az) >= 0;
  const quads = [[0, 2, 3, 1], [4, 5, 7, 6], [0, 1, 5, 4], [2, 6, 7, 3], [0, 4, 6, 2], [1, 3, 7, 5]];
  for (const [a, b, c2, d] of quads) {
    if (handed) { s.tri(idx[a], idx[b], idx[c2], o); s.tri(idx[a], idx[c2], idx[d], o); } else { s.tri(idx[a], idx[c2], idx[b], o); s.tri(idx[a], idx[d], idx[c2], o); }
  }
}

/** Catmull–Rom resample of a polyline into `n` points (inclusive ends). */
export function resample(pts: V3[], n: number): V3[] {
  if (pts.length < 2) return Array.from({ length: n }, () => pts[0]);
  const out: V3[] = [];
  const segs = pts.length - 1;
  for (let k = 0; k < n; k++) {
    const t = (k / (n - 1)) * segs;
    const i = Math.min(segs - 1, Math.floor(t));
    const f = t - i;
    const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
    const f2 = f * f, f3 = f2 * f;
    const c = (a: number, b: number, cc: number, d: number): number => 0.5 * (2 * b + (-a + cc) * f + (2 * a - 5 * b + 4 * cc - d) * f2 + (-a + 3 * b - 3 * cc + d) * f3);
    out.push([c(p0[0], p1[0], p2[0], p3[0]), c(p0[1], p1[1], p2[1], p3[1]), c(p0[2], p1[2], p2[2], p3[2])]);
  }
  return out;
}

/** Linear-blend skin for a point between two bone pivots by projection (smoothstep ±20 % around the joint, creatures §B6). */
export function segSkin(boneA: number, boneB: number, pa: V3, pb: V3, p: V3, width = 0.2): Skin {
  const ab = sub(pb, pa);
  const L2 = dot(ab, ab) || 1;
  const t = dot(sub(p, pa), ab) / L2; // 0 at A, 1 at B
  const s0 = 1 - width, s1 = 1 + width;
  const k = Math.max(0, Math.min(1, (t - s0) / (s1 - s0)));
  return blend(boneA, boneB, k * k * (3 - 2 * k));
}
